// src/modules/accounting/constants.js

/**
 * Mirror of the JOURNAL_ENTRY_SOURCES enum from the backend
 * (models/accounting/JournalEntry.js).
 *
 * Used for filter dropdowns in the Journal Entries page.
 */
export const JOURNAL_ENTRY_SOURCES = [
  'MANUAL',
  'FUND_CONTRIBUTION',
  'FUND_SETTLEMENT',
  'FUND_TRANSFER',
  'EXPENSE',
  'EXPENSE_PAYMENT',
  'SMELTER_DISPATCH',
  'SMELTER_REVENUE',
  'SMELTER_GST',
  'GOLD_PURCHASE',
  'GOLD_SALE',
  'BILLING',
  'ADVANCE_PAYMENT',
  'PROFIT_ALLOCATION',
  'LOSS_ALLOCATION',
  'PLEDGE_SETTLEMENT',
  'PROCESSING_FEE',
  'MANUAL_DEPOSIT',
  'MANUAL_WITHDRAWAL',
  'STOCK_ADJUSTMENT',
  'GST_ADJUSTMENT',
  'FUND_LOAD',
  'SYSTEM',
];

export const ACCOUNT_TYPES = [
  'ASSET',
  'LIABILITY',
  'EQUITY',
  'REVENUE',
  'EXPENSE',
];

/**
 * Mirror of ACCOUNT_NATURES from the backend (constants/accountNatures.js).
 * Nature is what users pick; the coarse `type` and `normalBalance` are derived
 * from it server-side. Keep this list in sync with the backend.
 */
export const ACCOUNT_NATURES = [
  // ── Balance Sheet ──
  { value: 'CURRENT_ASSET',                 label: 'Current Assets',                  type: 'ASSET',     normalBalance: 'DEBIT',  statement: 'Balance Sheet' },
  { value: 'NON_CURRENT_ASSET',             label: 'Non-Current Assets',              type: 'ASSET',     normalBalance: 'DEBIT',  statement: 'Balance Sheet' },
  { value: 'CURRENT_LIABILITY',             label: 'Current Liabilities',             type: 'LIABILITY', normalBalance: 'CREDIT', statement: 'Balance Sheet' },
  { value: 'NON_CURRENT_LIABILITY',         label: 'Non-Current Liabilities',         type: 'LIABILITY', normalBalance: 'CREDIT', statement: 'Balance Sheet' },
  { value: 'EQUITY',                        label: 'Equity',                          type: 'EQUITY',    normalBalance: 'CREDIT', statement: 'Balance Sheet' },
  // ── Profit & Loss ──
  { value: 'REVENUE_FROM_OPERATIONS',       label: 'Revenue from Operations',         type: 'REVENUE',   normalBalance: 'CREDIT', statement: 'Profit & Loss' },
  { value: 'OTHER_INCOME',                  label: 'Other Income',                    type: 'REVENUE',   normalBalance: 'CREDIT', statement: 'Profit & Loss' },
  { value: 'DIRECT_COSTS',                  label: 'Direct Costs',                    type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'EMPLOYEE_BENEFITS_EXPENSE',     label: 'Employee Benefits Expense',       type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'ADMINISTRATIVE_EXPENSES',       label: 'Administrative Expenses',         type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'SELLING_DISTRIBUTION_EXPENSES', label: 'Selling & Distribution Expenses', type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'FINANCE_COSTS',                 label: 'Finance Costs',                   type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'DEPRECIATION_AMORTIZATION',     label: 'Depreciation & Amortization',     type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'OTHER_EXPENSES',                label: 'Other Expenses',                  type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
  { value: 'INCOME_TAX_EXPENSE',            label: 'Income Tax Expense',              type: 'EXPENSE',   normalBalance: 'DEBIT',  statement: 'Profit & Loss' },
];

export const NATURE_MAP = ACCOUNT_NATURES.reduce((acc, n) => { acc[n.value] = n; return acc; }, {});

/**
 * Mirror of EXPENSE_CATEGORIES from the backend (models/accounting/CompanyExpense.js).
 *
 * `account` is the Chart-of-Accounts code the category usually posts to. It only
 * pre-selects the account in the entry form — the user can point any expense at
 * any active expense account, and the chosen code is what actually gets debited.
 */
export const EXPENSE_CATEGORIES = [
  { value: 'SALARY',                 label: 'Salary',                 account: '5100' },
  { value: 'RENT',                   label: 'Rent',                   account: '5200' },
  { value: 'ELECTRICITY',            label: 'Electricity',            account: '5200' },
  { value: 'INTERNET_TELECOM',       label: 'Internet & Telecom',     account: '5200' },
  { value: 'OFFICE_SUPPLIES',        label: 'Office Supplies',        account: '5300' },
  { value: 'MARKETING_ONLINE',       label: 'Marketing (Online)',     account: '5300' },
  { value: 'MARKETING_OFFLINE',      label: 'Marketing (Offline)',    account: '5300' },
  { value: 'TRAVEL',                 label: 'Travel',                 account: '5400' },
  { value: 'INSURANCE',              label: 'Insurance',              account: '5500' },
  { value: 'LEGAL_COMPLIANCE',       label: 'Legal & Compliance',     account: '5300' },
  { value: 'GOLD_TESTING',           label: 'Gold Testing / Assay',   account: '5300' },
  { value: 'PACKAGING_LOGISTICS',    label: 'Packaging & Logistics',  account: '5400' },
  { value: 'SECURITY',               label: 'Security',               account: '5300' },
  { value: 'MAINTENANCE',            label: 'Maintenance',            account: '5300' },
  { value: 'SOFTWARE_SUBSCRIPTIONS', label: 'Software / SaaS',        account: '5300' },
  { value: 'BANK_CHARGES',           label: 'Bank Charges',           account: '5700' },
  { value: 'TAXES_DUTIES',           label: 'Taxes & Duties',         account: '5950' },
  { value: 'DEPRECIATION',           label: 'Depreciation',           account: '5600' },
  { value: 'MISCELLANEOUS',          label: 'Miscellaneous',          account: '5900' },
];

export const EXPENSE_CATEGORY_MAP = EXPENSE_CATEGORIES.reduce((acc, c) => { acc[c.value] = c; return acc; }, {});

/** Fallback debit account when a category has no mapping — 5300 Office & Administrative. */
export const DEFAULT_EXPENSE_ACCOUNT_CODE = '5300';

export const EXPENSE_PAYMENT_MODES = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE', 'CREDIT_CARD', 'AUTO_DEBIT'];
